import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../util/common';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserHasListings } from '../../ducks/user.duck';
import { getStripeAccountData } from '../../util/stripeConnect';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { stripeCustomer } from '../PaymentMethodsPage/PaymentMethodsPage.duck';

const PAYMENT_HISTORY_PAGE_SIZE = 50;

const entityRefs = entities =>
  (entities || []).map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const paymentHistoryQueryParams = () => ({
  only: 'order',
  processNames: getSupportedProcessesInfo().map(p => p.name),
  include: ['listing', 'provider', 'booking'],
  'fields.transaction': [
    'processName',
    'processVersion',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'lineItems',
    'protectedData',
    'createdAt',
  ],
  'fields.booking': ['start', 'end', 'displayStart', 'displayEnd'],
  'fields.listing': ['title', 'publicData.listingType'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
  page: 1,
  perPage: PAYMENT_HISTORY_PAGE_SIZE,
  sort: 'lastTransitionedAt',
});

// ================ Async thunks ================ //

const savePayoutDetailsPayloadCreator = (
  { values, isUpdateCall },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      return response;
    })
    .catch(() => {
      return rejectWithValue('Failed to save payout details');
    });
};
export const savePayoutDetailsThunk = createAsyncThunk(
  'StripePayoutPage/savePayoutDetails',
  savePayoutDetailsPayloadCreator
);
// Backward compatible wrapper function
export const savePayoutDetails = (values, isUpdateCall) => dispatch => {
  return dispatch(savePayoutDetailsThunk({ values, isUpdateCall })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
  paymentHistoryRefs: [],
  fetchPaymentHistoryInProgress: false,
  fetchPaymentHistoryError: null,
};

const stripePayoutPageSlice = createSlice({
  name: 'StripePayoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
    paymentHistoryRequested: state => {
      state.fetchPaymentHistoryInProgress = true;
      state.fetchPaymentHistoryError = null;
    },
    paymentHistorySuccess: (state, action) => {
      state.fetchPaymentHistoryInProgress = false;
      state.paymentHistoryRefs = action.payload || [];
    },
    paymentHistoryError: (state, action) => {
      state.fetchPaymentHistoryInProgress = false;
      state.fetchPaymentHistoryError = action.payload;
      state.paymentHistoryRefs = [];
    },
  },
  extraReducers: builder => {
    builder
      // Save Payout Details cases
      .addCase(savePayoutDetailsThunk.pending, state => {
        state.payoutDetailsSaveInProgress = true;
      })
      .addCase(savePayoutDetailsThunk.fulfilled, state => {
        state.payoutDetailsSaveInProgress = false;
        state.payoutDetailsSaved = true;
      })
      .addCase(savePayoutDetailsThunk.rejected, state => {
        state.payoutDetailsSaveInProgress = false;
      });
  },
});

// Export the action creators
export const {
  setInitialValues,
  paymentHistoryRequested,
  paymentHistorySuccess,
  paymentHistoryError,
} = stripePayoutPageSlice.actions;

export default stripePayoutPageSlice.reducer;

// ================ Load Data ================ //

const isBusyError = error => isTooManyRequestsError(error) || error?.status === 429;

const swallowBusy = promise =>
  Promise.resolve(promise).catch(error => {
    if (isBusyError(error)) {
      return null;
    }
    throw error;
  });

/**
 * Payments page load. Read-only: never write listings or Stripe capabilities.
 * Extra currentUser / stripeAccount fetches are skipped when the store already
 * has them so this page does not 429 Sharetribe and trip the full-screen busy UI.
 */
export const loadData = () => (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const stripeState = state.stripeConnectAccount || {};
  const methods = state.PaymentMethodsPage || {};
  const payments = state.StripePayoutPage || {};
  const jobs = [];

  const knownListing =
    state.user?.currentUserHasListings && state.user?.currentUserOwnListing;
  if (!knownListing) {
    jobs.push(Promise.resolve(dispatch(fetchCurrentUserHasListings())).catch(() => null));
  }

  // Always settle stripeCustomerFetched. Skipping this when a card was already
  // cached (e.g. after Checkout) left the section on "Loading your card…".
  // The thunk short-circuits (and still marks fetched) when the card is cached.
  if (!methods.stripeCustomerFetched) {
    jobs.push(swallowBusy(dispatch(stripeCustomer({ enforce: false }))).catch(() => null));
  }

  const hasExpandedAccount = !!getStripeAccountData(stripeState.stripeAccount);
  if (currentUser?.stripeAccount && !hasExpandedAccount && !stripeState.fetchStripeAccountInProgress) {
    jobs.push(
      swallowBusy(
        dispatch(fetchStripeAccount({ skipCapabilityUpdate: true, skipIfCached: true }))
      ).catch(() => null)
    );
  }

  const hasHistory = (payments.paymentHistoryRefs || []).length > 0;
  if (!hasHistory) {
    dispatch(paymentHistoryRequested());
    jobs.push(
      sdk.transactions
        .query(paymentHistoryQueryParams())
        .then(response => {
          if (response?.data?.data?.length) {
            dispatch(addMarketplaceEntities(response));
          }
          dispatch(paymentHistorySuccess(entityRefs(response?.data?.data || [])));
          return response;
        })
        .catch(e => {
          dispatch(paymentHistoryError(storableError(e)));
          return null;
        })
    );
  }

  return Promise.all(jobs).then(() => null);
};
